import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Space,
  Typography,
  Radio,
  Checkbox,
  Select,
  Progress,
  Divider,
  Tag,
  Spin,
  Image,
  Row,
  Col,
  App
} from 'antd';
import { sanitizeHtml } from '../utils/sanitize';
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import questionnaireService from '../services/questionnaireService';
import campaignService from '../services/campaignService';
import settingsService from '../services/settingsService';
import { generateFingerprint } from '../services/fingerprint';
import { Content } from 'antd/es/layout/layout';
import { getTextSizePx } from '../constants/quizDesign';

const { Option } = Select;
const { Title, Text } = Typography;

const DEFAULT_THEME_BACKGROUND = 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)';
const DEFAULT_THEME_TEXT = '#ffffff';

const formatDateTimeForDisplay = (value) => {
  if (!value) {
    return null;
  }
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.warn('Failed to format date:', value, error);
    return null;
  }
};

const QuestionnairePreviewPage = () => {
  const navigate = useNavigate();
  const { id, targetId } = useParams();
  const location = useLocation();
  const { message: messageApi } = App.useApp();

  // Determine if this is campaign quiz mode or regular preview mode
  const isCampaignMode = location.pathname.includes('/campaign-quiz/');
  const routeParam = isCampaignMode ? targetId : id;

  const [questionnaire, setQuestionnaire] = useState(null);
  const [campaignData, setCampaignData] = useState(null); // For campaign mode
  const [globalSettings, setGlobalSettings] = useState(null); // For celebration GIFs from settings
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1 to introPages.length = intro pages, then questions, -1 = results
  const [userAnswers, setUserAnswers] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(!isCampaignMode); // Preview mode unless it's campaign
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [celebrationGif, setCelebrationGif] = useState(null); // Random celebration GIF for results page
  const [blinkingAnswer, setBlinkingAnswer] = useState(null); // Track which answer is blinking
  const [isStarting, setIsStarting] = useState(false); // Track if quiz is being started
  const [isTransitioning, setIsTransitioning] = useState(false); // Track fade transition state
  const getViewportInfo = () => {
    if (typeof window === 'undefined') {
      return {
        isMobile: false,
        isTablet: false
      };
    }

    const width = window.innerWidth;
    return {
      isMobile: width <= 768,
      isTablet: width <= 992
    };
  };

  const [viewport, setViewport] = useState(getViewportInfo);
  const { isMobile, isTablet } = viewport;
  const [randomizedAnswers, setRandomizedAnswers] = useState({}); // Store randomized answer orders per question
  const autoStartRef = useRef(false);
  const quizCompletionLockRef = useRef(false);
  const questionFetchPromiseRef = useRef(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [questionFetchError, setQuestionFetchError] = useState(null);
  const [isPreparingQuiz, setIsPreparingQuiz] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null); // Track when current quiz session started (for accurate duration)
  const [campaignAccessError, setCampaignAccessError] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);

  const deriveCampaignAccessError = useCallback(() => {
    if (!isCampaignMode) {
      return null;
    }

    const createAccessError = (type, title, description, severity = 'error', meta = {}) => ({
      type,
      title,
      description,
      toastMessage: description,
      severity,
      meta
    });

    if (!campaignData) {
      return null;
    }

    const campaign = campaignData.campaign;
 const linkExpirySource= ['expired', 'revoked'].includes(campaignData.status || '')
      || campaignData?.expirationDate
      || campaign?.endDate;
    const formattedLinkExpiry = formatDateTimeForDisplay(campaignData.campaign?.endDate);

    const normalizedTargetStatus = (campaignData.status || '').toLowerCase();

   
    

    if (['expired', 'revoked'].includes(normalizedTargetStatus)) {
      return createAccessError(
        'target-expired',
        'Quiz link expired',
        formattedLinkExpiry
          ? `This quiz link expired on ${formattedLinkExpiry}. Please contact your administrator for a fresh invite.`
          : 'This quiz link has expired. Please contact your administrator for a fresh invite.',
        'warning',
        formattedLinkExpiry ? { expiryDate: formattedLinkExpiry } : undefined
      );
    }

    if (!campaign) {
      return null;
    }

    const now = Date.now();
    const normalizedCampaignStatus = (campaign.status || '').toLowerCase();
    if (['cancelled', 'archived', 'inactive'].includes(normalizedCampaignStatus)) {
      return createAccessError(
        'campaign-unavailable',
        'Campaign unavailable',
        'This campaign is no longer available. Please reach out to your administrator for assistance.'
      );
    }

    const startDate = campaign.startDate ? new Date(campaign.startDate) : null;
    if (startDate && !Number.isNaN(startDate.getTime()) && now < startDate.getTime()) {
      const formatted = formatDateTimeForDisplay(startDate);
      return createAccessError(
        'campaign-not-started',
        'Campaign not open yet',
        formatted
          ? `This campaign opens on ${formatted}. Please try again later.`
          : 'This campaign is not open yet. Please try again later.',
        'info'
      );
    }

    const endDate = campaign.endDate ? new Date(campaign.endDate) : null;
    if (endDate && !Number.isNaN(endDate.getTime()) && now > endDate.getTime()) {
      const formatted = formatDateTimeForDisplay(endDate);
      return createAccessError(
        'campaign-expired',
        'Quiz no longer available',
        formatted
          ? `This quiz expired on ${formatted}. Please contact your administrator for a new invitation.`
          : 'This quiz has expired. Please contact your administrator for a new invitation.',
        'warning',
        { expiryDate: formatted }
      );
    }

    return null;
  }, [isCampaignMode, campaignData]);

  useEffect(() => {
    setCampaignAccessError(deriveCampaignAccessError());
  }, [deriveCampaignAccessError]);

  const organizationTheme = isCampaignMode ? campaignData?.participantId?.businessUnit : null;
  const shouldUseOrganizationTheme = Boolean(
    isCampaignMode && organizationTheme?.overrideQuestionnaireDesign
  );

  const getDesignValue = (key, fallback = null) => {
    const organizationValue = organizationTheme?.[key];
    const questionnaireValue = questionnaire?.[key];

    if (shouldUseOrganizationTheme) {
      return organizationValue ?? questionnaireValue ?? fallback;
    }

    return questionnaireValue ?? organizationValue ?? fallback;
  };

  const getTransitionStyle = () => ({
    opacity: isTransitioning ? 0 : 1,
    transition: 'opacity 0.6s ease-in-out'
  });

  const getAnswerLetter = (index) => String.fromCharCode(65 + index);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.replace('www.', '');
      let videoId = null;

      if (hostname === 'youtu.be') {
        videoId = parsed.pathname.slice(1);
      } else if (hostname.endsWith('youtube.com')) {
        if (parsed.searchParams.get('v')) {
          videoId = parsed.searchParams.get('v');
        } else if (parsed.pathname.startsWith('/embed/')) {
          videoId = parsed.pathname.replace('/embed/', '').split('/')[0];
        } else if (parsed.pathname.startsWith('/shorts/')) {
          videoId = parsed.pathname.replace('/shorts/', '').split('/')[0];
        }
      }

      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
      }
    } catch (error) {
      console.warn('Invalid YouTube URL provided:', url, error);
    }
    return null;
  };

  const hasRichContent = (html) => {
    if (typeof html !== 'string') {
      return false;
    }
    const stripped = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return stripped.length > 0;
  };

  const renderHtmlContent = (html, style = {}) => {
    if (!hasRichContent(html)) {
      return null;
    }
    return (
      <div
        className="rich-text-content"
        style={style}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
      />
    );
  };

  const parseColorToRgb = (color) => {
    if (typeof color !== 'string') {
      return null;
    }

    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const normalized = hex.length === 3
        ? hex.split('').map((char) => char + char).join('')
        : hex;
      const int = parseInt(normalized, 16);
      if (Number.isNaN(int)) {
        return null;
      }
      return {
        r: (int >> 16) & 255,
        g: (int >> 8) & 255,
        b: int & 255
      };
    }

    const rgbMatch = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
      return {
        r: Number(rgbMatch[1]),
        g: Number(rgbMatch[2]),
        b: Number(rgbMatch[3])
      };
    }

    return null;
  };

  const getReadableTextColor = (color, fallback = '#000000') => {
    const rgb = parseColorToRgb(color);
    if (!rgb) {
      return fallback;
    }

    const luminance = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return luminance >= 150 ? '#000000' : '#ffffff';
  };

  const resolveButtonColor = (fallbackTextColor = DEFAULT_THEME_TEXT) => {
    const fallbackColor = getDesignValue('textColor', fallbackTextColor) || fallbackTextColor;
    const buttonColor = getDesignValue('buttonColor', null);
    return buttonColor || fallbackColor;
  };

  const resolveButtonTextColor = (buttonColor, fallback = '#000000') => {
    const explicitColor = getDesignValue('buttonTextColor', null);
    if (explicitColor) {
      return explicitColor;
    }

    if (buttonColor) {
      return getReadableTextColor(buttonColor, fallback);
    }

    return fallback;
  };

  const welcomeTextSizeKey = getDesignValue('welcomeTextSize', 'md') || 'md';
  const questionTextSizeKey = getDesignValue('questionTextSize', 'md') || 'md';
  const welcomeFontSizePx = getTextSizePx('welcome', welcomeTextSizeKey);
  const questionFontSizePx = getTextSizePx('question', questionTextSizeKey);
  const answerFontSizePx = getTextSizePx('answer', questionTextSizeKey);
  const buttonFontSizePx = answerFontSizePx;
  const welcomeFontSize = `${welcomeFontSizePx}px`;
  const questionFontSize = `${questionFontSizePx}px`;
  const answerFontSize = `${answerFontSizePx}px`;
  const buttonFontSize = `${buttonFontSizePx}px`;
  const questionMetaFontSize = `${Math.max(questionFontSizePx - 2, 14)}px`;
  const answerMetaFontSize = `${Math.max(answerFontSizePx - 2, 12)}px`;

  const normalizeQuestionnairePayload = (payload) => {
    if (!payload) {
      return null;
    }
    return {
      ...payload,
      questions: Array.isArray(payload.questions) ? payload.questions : []
    };
  };

  // Shuffle array function (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get answers for a question (randomized if enabled)
  const getAnswersForQuestion = useCallback((question) => {
    if (!question) return [];
    
    // If randomization is enabled for this question
    if (question.randomizeAnswers) {
      // Check if we already have a randomized order for this question
      if (randomizedAnswers[question.id]) {
        return randomizedAnswers[question.id];
      }
      
      // Return original answers - the useEffect will handle shuffling
      return question.answers;
    }
    
    // Return original order if randomization is not enabled
    return question.answers;
  }, [randomizedAnswers]);

  // Helper: check if an answer image should be shown in a given context
  // context: 'question' (during quiz) or 'results' (on results screen)
  const shouldShowAnswerImage = useCallback((answer, context) => {
    if (!answer?.image) return false;
    const visibility = answer.imageVisibility || 'both';
    return visibility === 'both' || visibility === context;
  }, []);

  // Pre-shuffle answers for questions that need randomization when questions are loaded
  useEffect(() => {
    if (!questionnaire?.questions) return;
    
    const newRandomizedAnswers = {};
    let hasChanges = false;
    
    questionnaire.questions.forEach(question => {
      if (question.randomizeAnswers && !randomizedAnswers[question.id]) {
        newRandomizedAnswers[question.id] = shuffleArray(question.answers);
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      setRandomizedAnswers(prev => ({ ...prev, ...newRandomizedAnswers }));
    }
  }, [questionnaire?.questions]);

  // Helper functions for step calculations
  const getIntroductionPages = () => questionnaire?.introductionPages || [];
  const getTotalIntroSteps = () => 1 + getIntroductionPages().length; // Main intro + intro pages
  const isMainIntro = () => currentStep === 0;
  const isIntroductionPage = () => currentStep > 0 && currentStep < getTotalIntroSteps();
  const isQuestionStep = () => currentStep >= getTotalIntroSteps();
  const getCurrentIntroPageIndex = () => currentStep - 1; // currentStep 1 = introPage 0
  const getCurrentQuestionIndex = () => currentStep - getTotalIntroSteps(); // Adjust for intro steps

  useEffect(() => {
    questionFetchPromiseRef.current = null;
    setQuestionsLoaded(false);
    setQuestionFetchError(null);
    setRandomizedAnswers({});

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (isCampaignMode) {
          const fp = generateFingerprint();
          const response = await campaignService.getCampaignTarget(routeParam, { include: 'meta' }, { fingerprint: fp });
          if (response.success) {
            setCampaignData(response.data);
            const normalized = normalizeQuestionnairePayload(response.data.campaign?.questionnaire);
            setQuestionnaire(normalized);
            const hasQuestions = Array.isArray(normalized?.questions) && normalized.questions.length > 0;
            setQuestionsLoaded(hasQuestions);
          } else {
            throw new Error(response.message || 'Failed to load campaign quiz');
          }
        } else {
          const response = await questionnaireService.getById(routeParam);
          if (response.success) {
            const normalized = normalizeQuestionnairePayload(response.data);
            setQuestionnaire(normalized);
            setQuestionsLoaded(true);
          } else {
            throw new Error(response.message || 'Failed to load quiz');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        messageApi.error(isCampaignMode ? 'Failed to load campaign quiz' : 'Failed to load quiz');
        if (isCampaignMode) {
          navigate('/');
        } else {
          window.close();
        }
      } finally {
        setLoading(false);
      }
    };

    if (routeParam) {
      fetchData();
    }
  }, [routeParam, isCampaignMode, navigate, messageApi]);

  // Fetch global settings for celebration GIFs (using public endpoint - no auth needed)
  useEffect(() => {
    const fetchCelebrationGifs = async () => {
      try {
        const result = await settingsService.getPublicCelebrationGifs();
        if (result.success && result.data) {
          setGlobalSettings({ celebrationGifs: result.data });
        }
      } catch (error) {
        console.error('Error loading celebration GIFs:', error);
      }
    };
    fetchCelebrationGifs();
  }, []);

  const loadCampaignQuestions = useCallback(async ({ silent = false } = {}) => {
    if (!isCampaignMode || !routeParam) {
      return true;
    }

    if (questionsLoaded) {
      return true;
    }

    if (questionFetchPromiseRef.current) {
      return questionFetchPromiseRef.current;
    }

    const fetchPromise = (async () => {
      setQuestionsLoading(true);
      if (!silent) {
        setQuestionFetchError(null);
      }

      try {
        const response = await campaignService.getCampaignTarget(routeParam, { include: 'questions' }, { fingerprint: generateFingerprint() });
        if (!response.success) {
          throw new Error(response.message || 'Failed to load quiz questions');
        }

        const incomingQuestionnaire = response.data.campaign?.questionnaire || {};
        const nextQuestions = Array.isArray(incomingQuestionnaire.questions)
          ? incomingQuestionnaire.questions
          : [];

        setQuestionnaire(prev => {
          const base = prev ? { ...prev } : {};
          return {
            ...base,
            ...incomingQuestionnaire,
            questions: nextQuestions
          };
        });
        setQuestionsLoaded(true);
        if (!silent) {
          setQuestionFetchError(null);
        }
        setRandomizedAnswers({});
        return true;
      } catch (error) {
        console.error('Error loading campaign questions:', error);
        const message = error.message || 'Failed to load quiz questions';
        if (!silent) {
          setQuestionFetchError(message);
          messageApi.error(message);
        }
        return false;
      } finally {
        setQuestionsLoading(false);
        questionFetchPromiseRef.current = null;
      }
    })();

    questionFetchPromiseRef.current = fetchPromise;
    return fetchPromise;
  }, [isCampaignMode, routeParam, questionsLoaded, messageApi]);

  useEffect(() => {
    if (!isCampaignMode || !routeParam) {
      return;
    }
    if (!questionnaire || questionsLoaded || questionsLoading) {
      return;
    }
    loadCampaignQuestions({ silent: true });
  }, [isCampaignMode, routeParam, questionnaire, questionsLoaded, questionsLoading, loadCampaignQuestions]);

  useEffect(() => {
    if (isQuestionStep()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setViewport(getViewportInfo());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const startQuiz = useCallback(async ({ silent = false } = {}) => {
    if (!isCampaignMode || campaignAccessError) {
      if (!silent && campaignAccessError) {
        messageApi.error(campaignAccessError.toastMessage || campaignAccessError.description || 'Campaign unavailable');
      }
      return;
    }

    setIsStarting(true);
    try {
      const response = await campaignService.startCampaignQuiz(routeParam, { fingerprint: generateFingerprint() });
      if (response.success) {
        setCampaignData(prev => ({
          ...prev,
          status: 'in-progress',
          startedAt: response.data?.startedAt || new Date().toISOString()
        }));
        setSessionStartTime(Date.now()); // Track session start time for duration
        if (!silent) {
          messageApi.success('Quiz started successfully!');
        }
      } else if (!silent) {
        messageApi.error(response.message || 'Failed to start quiz');
      }
    } catch (error) {
      console.error('Error starting quiz:', error);
      if (!silent) {
        messageApi.error('Failed to start quiz');
      }
    } finally {
      setIsStarting(false);
    }
  }, [campaignAccessError, isCampaignMode, messageApi, routeParam]);

  // Auto-start quiz when accessed (mark as started)
  useEffect(() => {
    const autoStartQuiz = async () => {
      if (isCampaignMode && campaignData?.status === 'pending' && !isStarting && !campaignAccessError) {
        await startQuiz();
      }
    };

    if (campaignData && !campaignAccessError) {
      autoStartQuiz();
    }
  }, [campaignData, isCampaignMode, isStarting, startQuiz, campaignAccessError]);

  const handleAnswerChange = (questionId, answerId) => {
    // Set blinking effect
    setBlinkingAnswer(`${questionId}-${answerId}`);
    
    // Update the answer
    setUserAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: answerId };
      console.log('Updated userAnswers:', newAnswers);
      
      // Auto-advance with the updated answers
      setTimeout(() => {
        autoAdvanceToNext(newAnswers);
      }, 1200);
      
      return newAnswers;
    });
    
    // Clear blinking effect after animation
    setTimeout(() => {
      setBlinkingAnswer(null);
    }, 300);
  };

  const autoAdvanceToNext = (currentAnswers = null) => {
    if (!questionnaire?.questions || questionnaire.questions.length === 0) {
      return;
    }
    const totalIntroSteps = getTotalIntroSteps();
    const totalQuestions = questionnaire.questions.length;
    const lastQuestionStep = totalIntroSteps + totalQuestions - 1;
    
    if (currentStep < lastQuestionStep) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === lastQuestionStep) {
      // Show submit confirmation instead of completing immediately
      setShowSubmitConfirmation(true);
    }
  };

  const handleMultipleAnswerChange = (questionId, answerIds) => {
    // Set blinking effect for the last changed answer
    const currentAnswers = userAnswers[questionId] || [];
    const newAnswers = Array.isArray(answerIds) ? answerIds : [answerIds];
    
    // Find which answer was just added/removed
    const addedAnswer = newAnswers.find(id => !currentAnswers.includes(id));
    const removedAnswer = currentAnswers.find(id => !newAnswers.includes(id));
    const changedAnswer = addedAnswer || removedAnswer;
    
    if (changedAnswer) {
      setBlinkingAnswer(`${questionId}-${changedAnswer}`);
      setTimeout(() => {
        setBlinkingAnswer(null);
      }, 300);
    }
    
    setUserAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: answerIds };
      console.log('Updated userAnswers (multiple):', newAnswers);
      return newAnswers;
    });
  };

  // Helper function to get selected answer(s) for a question
  const getSelectedAnswers = (question, userAnswer) => {
    if (!userAnswer) return [];
    
    if (question.type === 'single') {
      return question.answers.filter(answer => answer.id === userAnswer);
    } else {
      return question.answers.filter(answer => userAnswer.includes(answer.id));
    }
  };

  const nextStep = () => {
    if (!questionnaire?.questions || questionnaire.questions.length === 0) {
      messageApi.warning('Questions are still loading. Please wait a moment.');
      return;
    }
    const totalIntroSteps = getTotalIntroSteps();
    const totalQuestions = questionnaire.questions.length;
    const lastQuestionStep = totalIntroSteps + totalQuestions - 1;
    
    // If we're on a question step, validate that an answer is selected
    if (isQuestionStep()) {
      const currentQuestion = questionnaire.questions[getCurrentQuestionIndex()];
      const userAnswer = userAnswers[currentQuestion.id];
      
      if (!userAnswer || (Array.isArray(userAnswer) && userAnswer.length === 0)) {
        messageApi.warning('Please select an answer before proceeding');
        return;
      }
    }
    
    if (currentStep < lastQuestionStep) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === lastQuestionStep) {
      // Show submit confirmation instead of completing immediately
      setShowSubmitConfirmation(true);
    }
  };

  // Function to start the quiz and update status to in-progress
  const handlePrimaryAction = useCallback(async () => {
    if (!questionnaire) {
      return;
    }

    if (campaignAccessError) {
      messageApi.error(campaignAccessError.toastMessage || campaignAccessError.description || 'Campaign unavailable');
      return;
    }

    setIsPreparingQuiz(true);
    try {
      if (isCampaignMode) {
        const questionsReady = await loadCampaignQuestions();
        if (!questionsReady) {
          return;
        }
      }

      if (isCampaignMode && !isPreviewMode && campaignData?.status === 'pending') {
        await startQuiz();
      }
      
      // Reset quiz state for retakes (when status is completed)
      if (isCampaignMode && campaignData?.status === 'completed') {
        setUserAnswers({});
        setQuizCompleted(false);
        setFinalScore(0);
        setShowSubmitConfirmation(false);
        quizCompletionLockRef.current = false;
      }

      const introPages = questionnaire.introductionPages || [];
      if (introPages.length > 0) {
        setCurrentStep(1);
      } else {
        setCurrentStep(1 + introPages.length);
      }
    } finally {
      setIsPreparingQuiz(false);
    }
  }, [campaignAccessError, campaignData, isCampaignMode, isPreviewMode, questionnaire, loadCampaignQuestions, startQuiz, messageApi]);

  useEffect(() => {
    if (!isCampaignMode || isPreviewMode || campaignAccessError) {
      return;
    }

    if (campaignData?.status !== 'pending') {
      return;
    }

    if (autoStartRef.current) {
      return;
    }

    autoStartRef.current = true;
    startQuiz({ silent: true });
  }, [campaignData?.status, isCampaignMode, isPreviewMode, startQuiz, campaignAccessError]);

  // Function to toggle preview mode for campaign quizzes
  const resetQuizCompletion = useCallback(() => {
    quizCompletionLockRef.current = false;
    setQuizCompleted(false);
    setFinalScore(0);
  }, []);

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
    setCurrentStep(0); // Reset to intro
    setUserAnswers({}); // Clear answers
    resetQuizCompletion();
  };

  // Function to restart quiz
  const restartQuiz = () => {
    setCurrentStep(0);
    setUserAnswers({});
    resetQuizCompletion();
    setSessionStartTime(Date.now()); // Track retake start time
  };

  // Function to close preview (for non-campaign mode)
  const closePreview = () => {
    if (window.opener) {
      window.close();
    } else {
      window.history.back();
    }
  };
  const openQuizInNewTab = async () => {
    if (!isCampaignMode) return;
    if (campaignAccessError) {
      messageApi.error(campaignAccessError.toastMessage || campaignAccessError.description || 'Campaign unavailable');
      return;
    }
    
    // Start the quiz first if not already started
    if (campaignData?.status === 'pending') {
      await startQuiz();
    }
    
    // Open in new tab
    const newTabUrl = window.location.href;
    window.open(newTabUrl, '_blank');
  };

  const completeQuiz = async (providedAnswers = null) => {
    if (quizCompletionLockRef.current) {
      return;
    }
    if (!questionnaire?.questions || questionnaire.questions.length === 0) {
      messageApi.error('Quiz questions are not available yet. Please try again in a moment.');
      return;
    }
    quizCompletionLockRef.current = true;
    // Use provided answers or fall back to state
    const answersToUse = providedAnswers || userAnswers;
    console.log('Complete quiz with answers:', answersToUse);
    console.log('Total questions:', questionnaire.questions.length);
    
    // Calculate score using the questionnaire's calculateScore method logic
    let correctCount = 0;
    let totalQuestions = questionnaire.questions.length;
    
    questionnaire.questions.forEach(question => {
      const userAnswer = answersToUse[question.id];
      if (!userAnswer) return;
      
      if (question.type === 'single') {
        // Single choice - check if the selected answer is correct
        const selectedAnswer = question.answers.find(answer => answer.id === userAnswer);
        if (selectedAnswer && selectedAnswer.isCorrect) {
          correctCount++;
        }
      } else if (question.type === 'multiple') {
        // Multiple choice - check if all correct answers are selected
        const correctAnswers = question.answers.filter(answer => answer.isCorrect).map(answer => answer.id);
        const selectedAnswers = Array.isArray(userAnswer) ? userAnswer : [userAnswer];
        
        const hasAllCorrect = correctAnswers.every(correctId => selectedAnswers.includes(correctId));
        const hasNoIncorrect = selectedAnswers.every(selectedId => {
          const answer = question.answers.find(a => a.id === selectedId);
          return answer && answer.isCorrect;
        });
        
        if (hasAllCorrect && hasNoIncorrect) {
          correctCount++;
        }
      }
    });
    
    const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
    setFinalScore(score);
    setQuizCompleted(true);
    
    // Select a random celebration GIF from global settings if available
    if (globalSettings?.celebrationGifs && globalSettings.celebrationGifs.length > 0) {
      const randomIndex = Math.floor(Math.random() * globalSettings.celebrationGifs.length);
      setCelebrationGif(globalSettings.celebrationGifs[randomIndex]);
    }
    
    // Submit results if in campaign mode and not in preview mode
    // Always submit - this tracks attempts for both first completion and retakes
    if (isCampaignMode && routeParam && !isPreviewMode) {
      const isRetake = campaignData?.status === 'completed';
      try {
        // Validate required data before submission
        if (!campaignData) {
          throw new Error('Campaign data is not available');
        }
        
        
        const answers = {};
        const responses = Object.entries(answersToUse).map(([questionId, answer]) => {
          answers[questionId] = answer;
          return {
            questionId,
            selectedAnswers: Array.isArray(answer) ? answer : [answer]
          };
        });
        
        // Extract IDs with proper fallbacks
        const campaignId = campaignData?.campaign?._id || campaignData?.campaignId?._id || campaignData?.campaignId;
        const participantId = campaignData?.participantId?._id || campaignData?.participantId;
        if (!campaignId) {
          throw new Error('Campaign ID is not available');
        }
        if (!participantId) {
          throw new Error('Participant ID is not available');
        }
        
        const submissionData = {
          targetId: routeParam,
          campaignId: campaignId,
          participantId: participantId,
          questionnaireId: questionnaire._id,
          fingerprint: generateFingerprint(),
          answers: answers,
          results: {
            correctAnswers: correctCount,
            wrongAnswers: totalQuestions - correctCount,
            totalQuestions: totalQuestions,
            scorePercentage: score
          },
          completedAt: new Date().toISOString(),
          timeTaken: Math.floor((Date.now() - (sessionStartTime || (campaignData?.startedAt ? new Date(campaignData.startedAt).getTime() : Date.now()))) / 1000),
          isRetake: isRetake
        };
        
        // Submit results with retry (1 retry on failure)
        let submitSuccess = false;
        for (let attempt = 0; attempt < 2 && !submitSuccess; attempt++) {
          try {
            await campaignService.submitCampaignQuizResults(submissionData);
            submitSuccess = true;
          } catch (retryErr) {
            if (attempt === 0) {
              console.warn('Submit attempt 1 failed, retrying...', retryErr.message);
              await new Promise(r => setTimeout(r, 1000)); // wait 1s before retry
            } else {
              throw retryErr;
            }
          }
        }
        
        // Update campaign data status (only update score/completedAt for first completion)
        if (!isRetake) {
          setCampaignData(prev => ({
            ...prev,
            status: 'completed',
            completedAt: new Date().toISOString(),
            score: score
          }));
        }
        
        messageApi.success(isRetake ? 'Retake recorded!' : 'Quiz completed successfully!');
      } catch (error) {
        console.error('Error submitting quiz results:', error);
        messageApi.error('Quiz completed but there was an error saving your results. Please check your connection.');
      }
      
      // Fetch leaderboard in background (don't block the results screen)
      setLeaderboardLoading(true);
      campaignService.getLeaderboard(routeParam)
        .then(leaderboardResponse => {
          if (leaderboardResponse.success && leaderboardResponse.data?.enabled) {
            setLeaderboardData(leaderboardResponse.data);
          }
        })
        .catch(leaderboardError => {
          console.error('Error fetching leaderboard:', leaderboardError);
        })
        .finally(() => {
          setLeaderboardLoading(false);
        });
    }
    
    setShowSubmitConfirmation(false);
    setCurrentStep(-1); // Set to results screen
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderIntroScreen = () => {
    // Use business unit theme for campaign mode, otherwise use questionnaire theme
    const backgroundColor = getDesignValue('backgroundColor', DEFAULT_THEME_BACKGROUND);
    const textColor = getDesignValue('textColor', DEFAULT_THEME_TEXT);
    const buttonColor = resolveButtonColor(textColor);
    const buttonTextColor = resolveButtonTextColor(buttonColor);
    const heroImage = questionnaire?.introImage
      || questionnaire?.heroImage
      || questionnaire?.image
      || null;
    const hasHeroImage = Boolean(heroImage);
    
    return (
      <div style={{
        background: backgroundColor,
        minHeight: isMobile ? 'auto' : '100vh',
        display: 'flex',
        ...getTransitionStyle()
      }}>
        <Row style={{
          minHeight: isMobile ? 'auto' : '100vh',
          height: isMobile ? 'auto' : '100vh',
          width: '100%'
        }}>
          {/* Right side - Image (exactly like login page) - Order changed for mobile */}
          {hasHeroImage && (
            <Col
              xs={{ span: 24, order: 1 }}
              lg={{ span: 12, order: 2 }}
              style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              padding: '0px',
              minHeight: isTablet ? '320px' : '100vh',
              height: isTablet ? '320px' : '100vh',
              boxSizing: 'border-box'
            }}
            >
              <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                <img
                  src={heroImage}
                  alt="Intro Hero"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </div>
            </Col>
          )}

          {/* Left side - Content (exactly like login page) - Order changed for mobile */}
          <Col
            xs={{ span: 24, order: hasHeroImage ? 2 : 1 }}
            lg={{ span: hasHeroImage ? 12 : 24, order: hasHeroImage ? 1 : 1 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: isMobile ? 'flex-start' : 'center',
              alignItems: isMobile ? 'center' : 'flex-start',
              padding: isMobile ? '24px' : '0px',
              paddingBottom: isMobile ? '48px' : '120px',
              minHeight: isMobile ? 'auto' : (isTablet ? '500px' : '100vh'),
              height: isMobile ? 'auto' : (isTablet ? '500px' : '100vh'),
              boxSizing: 'border-box'
            }}
          >
          
          <Content style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: isMobile ? 'flex-start' : 'center',
            minHeight: isMobile ? 'auto' : (isTablet ? '500px' : '100vh'),
            height: isMobile ? 'auto' : (isTablet ? '500px' : '100vh'),
            width: '100%'
          }}>
             <div style={{ maxWidth: '600px', width: '100%', textAlign: 'left', padding: isMobile ? '24px' : '40px' }}>
              {isCampaignMode && campaignData ? (
                <div style={{ marginBottom: '20px' }}>
                  <Text style={{ 
                    color: textColor, 
                    fontSize: welcomeFontSize,
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight:"bold"
                  }}>
                    Hi there {campaignData.participantId?.name}!
                  </Text>
                  
                  {/* Status indicator removed as requested */}
                </div>
              ) : (
                <Title level={3} style={{ 
                  color: textColor, 
                  opacity: 0.9,
                  textAlign: 'left',
                  fontSize: welcomeFontSize,
                  lineHeight: 1.2
                }}>
                  {questionnaire.title || 'Welcome to Quiz Preview'}
                </Title>
              )}
              
              {renderHtmlContent(questionnaire.context, {
                color: textColor,
                opacity: 0.9,
                lineHeight: '1.7',
                fontSize: questionFontSize,
                textAlign: 'left',
                marginBottom: '32px',
                wordBreak: 'break-word'
              }) || (
                questionnaire.description ? (
                  <Text style={{ 
                    color: textColor, 
                    textAlign: 'left',
                    marginBottom: '20px',
                    fontSize: questionFontSize
                  }}>
                    {questionnaire.description}
                  </Text>
                ) : !isCampaignMode ? (
                  <Text style={{ 
                    color: textColor, 
                    lineHeight: '1.5',
                    display: 'block',
                    marginBottom: '40px',
                    opacity: 0.85,
                    textAlign: 'center',
                    fontSize: questionFontSize
                  }}>
                    Navigate through all questions and see how the quiz appears to participants.
                  </Text>
                ) : null
              )}

              {campaignAccessError && (
                <div style={{ marginTop: '12px', marginBottom: '24px' }}>
                  <Text strong style={{ color: textColor, display: 'block', lineHeight: 1.6 }}>
                    <ExclamationCircleOutlined
                      style={{
                        marginRight: 8,
                        color:
                          campaignAccessError.severity === 'info'
                            ? '#1890ff'
                            : campaignAccessError.severity === 'error'
                              ? '#ff4d4f'
                              : '#faad14'
                      }}
                    />
                    {campaignAccessError.title}
                  </Text>
                  <Text style={{ color: textColor, opacity: 0.9, display: 'block', lineHeight: 1.6 }}>
                    {campaignAccessError.description}
                  </Text>
                  {campaignAccessError.meta?.expiryDate && (
                    <Text style={{ color: textColor, opacity: 0.85, fontSize: '13px' }}>
                      Expired: {campaignAccessError.meta.expiryDate}
                    </Text>
                  )}
                </div>
              )}

              {/* Main action button - exactly like login page */}
              <div style={{ marginTop: '40px' }}>
                <Button
                  style={{
                    backgroundColor: buttonColor,
                    borderColor: buttonColor,
                    color: buttonTextColor,
                    width: '100%',
                    marginBottom: isCampaignMode ? '12px' : '0',
                    fontWeight: '700',
                    fontSize: buttonFontSize
                  }}
                  size="large"
                  onClick={handlePrimaryAction}
                  loading={isStarting || isPreparingQuiz}
                  disabled={Boolean(campaignAccessError)}
                >
                  {isCampaignMode ? (
                    isPreviewMode ? "Preview Quiz >>" :
                    campaignData?.status === 'completed' ? "Retake Quiz >>" :
                    campaignData?.status === 'in-progress' ? "Continue Quiz >>" :
                    "Start Quiz >>"
                  ) : "Let's Get Started >>"}
                </Button>


                
                {/* Warning for completed quiz */}
                {isCampaignMode && campaignData?.status === 'completed' && (
                  <Text style={{ 
                    color: textColor, 
                    opacity: 0.7,
                    fontSize: '12px',
                    display: 'block',
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    You can retake this quiz. Your attempt will be recorded.
                  </Text>
                )}
                
                {/* Preview mode warning */}
                {isCampaignMode && isPreviewMode && (
                  <Text style={{ 
                    color: textColor, 
                    opacity: 0.7,
                    fontSize: '12px',
                    display: 'block',
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    🔍 Preview Mode: Your answers and results will not be saved.
                  </Text>
                )}

                {questionFetchError && (
                  <Text style={{
                    color: textColor,
                    fontSize: '12px',
                    display: 'block',
                    textAlign: 'center',
                    marginTop: '8px',
                    opacity: 0.8
                  }}>
                    {questionFetchError}
                  </Text>
                )}
              </div>
             
              
              {/* Secondary actions - only show at end/results */}
            </div>
          </Content>
   
          
        
         
            
          </Col>
        </Row>
      </div>
    );
  };

  const renderQuestion = (question, index) => {
    const questionNumber = index + 1;
    const userAnswer = userAnswers[question.id];
    const focusX = typeof question.imageFocus?.x === 'number' ? question.imageFocus.x : 50;
    const focusY = typeof question.imageFocus?.y === 'number' ? question.imageFocus.y : 50;
    const imageObjectPosition = `${focusX}% ${focusY}%`;
    const videoEmbedUrl = getYouTubeEmbedUrl(question.videoUrl);
    // Respect imageVisibility: hide question media when set to 'results' only
    const questionVisibility = question.imageVisibility || 'both';
    const showMediaOnQuestion = questionVisibility === 'both' || questionVisibility === 'question';
    const hasVideo = Boolean(videoEmbedUrl) && showMediaOnQuestion;
    const hasImage = Boolean(question?.image) && showMediaOnQuestion;
    const hasMedia = hasVideo || hasImage;
    
    // Use business unit theme for campaign mode, otherwise use questionnaire theme
    const backgroundColor = getDesignValue('backgroundColor', DEFAULT_THEME_BACKGROUND);
    const textColor = getDesignValue('textColor', DEFAULT_THEME_TEXT);
    const buttonColor = resolveButtonColor(textColor);
    const buttonTextColor = resolveButtonTextColor(buttonColor);
    
    // Full page style matching PDF design
    const pageStyle = {
      background: backgroundColor,
      minHeight: '100vh',
      padding: '0px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: textColor
    };

    const containerStyle = {
      backgroundColor: 'transparent',
      padding: isMobile ? '16px' : '0px',
      color: textColor,
      maxWidth: '100%'
    };

    const navMaxWidth = isMobile
      ? (hasMedia ? 360 : 320)
      : hasMedia
        ? 720
        : 640;
    const navBottomOffset = isMobile ? 80 : 100;
    const navPadding = isMobile ? 16 : 24;
    const prefersLightNav = getReadableTextColor(backgroundColor, '#000000') === '#000000';
    const navBackgroundColor = prefersLightNav ? 'rgba(255, 255, 255, 0.95)' : 'rgba(20, 20, 20, 0.8)';
    const navBorderColor = prefersLightNav ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.25)';
    const navProgressTextColor = prefersLightNav ? '#4a4a4a' : '#f5f5f5';

    const navContainerStyle = {
      width: '100%',
      maxWidth: `${navMaxWidth}px`,
      padding: isMobile ? '10px 12px' : '14px 24px',
      background: navBackgroundColor,
      backdropFilter: 'blur(22px)',
      boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
      border: `1px solid ${navBorderColor}`,
      borderRadius: 32,
      pointerEvents: 'auto',
      zIndex: 30
    };

    const navContentStyle = {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      gap: '16px',
      alignItems: 'center',
      justifyContent: 'space-between'
    };

    const navWrapperStyle = {
      position: 'relative',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      marginTop: '60px',
      paddingBottom: '40px',
      pointerEvents: 'none'
    };

    const questionContentWrapperStyle = {
      width: '100%',
      position: 'relative',
      paddingBottom: '0px',
      ...getTransitionStyle()
    };

    return (
      <div style={{
        background: backgroundColor,
        minHeight: '100vh',
        display: 'flex'}}>
        <div style={questionContentWrapperStyle}>
          <div >
            {/* Main Content Layout - Responsive Row/Col system */}
            <Row style={{ minHeight: '100vh' }}>
              {/* Question Media Section - Image or Video */}
          {hasMedia && (
            <Col
              xs={{ span: 24, order: 1 }}
              md={{ span: 12, order: 2 }}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
                padding: '0px',
                minHeight: isMobile ? '300px' : '100vh',
                height: isMobile ? '300px' : '100vh'
              }}
            >
              {hasVideo ? (
                <div style={{
                  width: '100%',
                  height: '100%',
                  minHeight: isMobile ? '300px' : '420px',
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  position: 'relative'
                }}>
                  <iframe
                    src={videoEmbedUrl}
                    title={`Question ${questionNumber} video`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 0
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: isMobile ? '300px' : '100vh',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={question.image}
                    alt="Question"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                </div>
              )}
            </Col>
          )}

           
              {/* Question Content Section - Order changed for mobile */}
              <Col 
                xs={{ span: 24, order: 1 }} 
                md={{ span: hasMedia ? 12 : 24, order: 1 }} 
                style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '0px',
                minHeight: isMobile ? (hasMedia ? 'auto' : '100vh') : '100vh'
              }}>
                <Content style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  padding: '0px'
                }}>
                     <div style={{
                       width: '100%',
                       maxWidth: hasMedia ? 640 : 720,
                       textAlign: 'left',
                       margin: '0 auto',
                       padding: isMobile ? '16px' : '24px'
                     }}>
                  {/* Question Header */}
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Text 
                        style={{ 
                          fontSize: questionMetaFontSize, 
                          color: textColor, 
                          opacity: 0.7, 
                          fontWeight: '600',
                          display: 'block',
                          marginBottom: '8px'
                        }}
                      >
                        QUESTION {questionNumber}
                      </Text>

                      <Text 
                        style={{ 
                          fontSize: questionFontSize, 
                          color: textColor, 
                          opacity: 0.9, 
                          fontWeight: '600',
                          display: 'block'
                        }}
                      >
                        {question.title}
                      </Text>

                      {question.description && (
                        <Text 
                          style={{ 
                            fontSize: '14px', 
                            color: textColor, 
                            opacity: 0.75, 
                            fontWeight: '400',
                            display: 'block',
                            marginTop: '8px',
                            fontStyle: 'italic'
                          }}
                        >
                          {question.description}
                        </Text>
                      )}
                    </div>

                  </div>

                  {/* Answer Options */}
                  <div style={{ marginBottom: '20px' }}>
                    {question.type === 'single' ? (
                      <Radio.Group 
                        value={userAnswer} 
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        style={{ width: '100%' }}
                      >
                        {/* Conditional layout based on whether answers have images visible during question */}
                        {getAnswersForQuestion(question).some(answer => shouldShowAnswerImage(answer, 'question')) ? (
                          // Responsive grid layout for answers with images
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '16px'
                          }}> 
                            {getAnswersForQuestion(question).map((answer, answerIndex) => (
                              <div 
                                key={answer.id}
                                className={`answer-option ${blinkingAnswer === `${question.id}-${answer.id}` ? 'blinking-answer' : ''}`}
                                style={{ 
                                  backgroundColor: userAnswer === answer.id ? `${textColor}15` : 'transparent',
                                  border: `3px solid ${userAnswer === answer.id ? textColor : `${textColor}30`}`,
                                  borderRadius: '12px',
                                  padding: '8px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  minHeight: '180px',
                                  textAlign: 'center',
                                  width: '100%'
                                }}
                                onClick={() => handleAnswerChange(question.id, answer.id)}
                              >
                                {/* Answer Image */}
                                {shouldShowAnswerImage(answer, 'question') && (
                                  <div
                                    style={{
                                      width: '100%',
                                      height: isMobile ? 120 : 240,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden',
                                      marginBottom: '5px',
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <img
                                      src={answer.image}
                                      alt="Answer"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Option Letter and Text */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{
                                    backgroundColor: userAnswer === answer.id ? textColor : 'transparent',
                                    color: userAnswer === answer.id ? backgroundColor.includes('52c41a') ? '#52c41a' : '#000' : textColor,
                                    border: `2px solid ${textColor}`,
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: answerMetaFontSize,
                                    flexShrink: 0
                                  }}>
                                    {String.fromCharCode(65 + answerIndex)}
                                  </div>
                                  
                                  <Text style={{ 
                                    fontSize: answerFontSize, 
                                    color: textColor, 
                                    fontWeight: '500'
                                  }}>
                                    {answer.text}
                                  </Text>
                                </div>
                                
                                {/* Hidden Radio */}
                                <Radio value={answer.id} style={{ display: 'none' }} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Simple vertical layout for text-only answers
                          <Space direction="vertical" style={{ width: '100%' }}>
                            {getAnswersForQuestion(question).map((answer, answerIndex) => (
                              <div 
                                key={answer.id}
                                className={`answer-option ${blinkingAnswer === `${question.id}-${answer.id}` ? 'blinking-answer' : ''}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '12px',
                                  border: `2px solid ${userAnswer === answer.id ? textColor : `${textColor}30`}`,
                                  borderRadius: '8px',
                                  backgroundColor: userAnswer === answer.id ? `${textColor}15` : 'transparent',
                                  margin: '8px 0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  fontSize: answerFontSize,
                                  fontWeight: '500'
                                }}
                                onClick={() => handleAnswerChange(question.id, answer.id)}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  width: '100%'
                                }}>
                                  <div style={{
                                    backgroundColor: userAnswer === answer.id ? textColor : 'transparent',
                                    color: userAnswer === answer.id ? (backgroundColor.includes('52c41a') ? '#52c41a' : '#000') : textColor,
                                    border: `2px solid ${textColor}`,
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: answerMetaFontSize,
                                    flexShrink: 0
                                  }}>
                                    {getAnswerLetter(answerIndex)}
                                  </div>
                                  <Text style={{ color: textColor, fontSize: answerFontSize }}>
                                    {answer.text}
                                  </Text>
                                </div>
                                <Radio value={answer.id} style={{ display: 'none' }} />
                              </div>
                            ))}
                          </Space>
                        )}
                      </Radio.Group>
                    ) : (
                      <Checkbox.Group 
                        value={userAnswer || []} 
                        onChange={(values) => handleMultipleAnswerChange(question.id, values)}
                        style={{ width: '100%' }}
                      >
                        {/* Conditional layout based on whether answers have images visible during question */}
                        {getAnswersForQuestion(question).some(answer => shouldShowAnswerImage(answer, 'question')) ? (
                          // Responsive grid layout for answers with images
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            gap: '16px'
                          }}>
                            {getAnswersForQuestion(question).map((answer, answerIndex) => (
                              <div 
                                key={answer.id}
                                className={`answer-option ${blinkingAnswer === `${question.id}-${answer.id}` ? 'blinking-answer' : ''}`}
                                style={{ 
                                  backgroundColor: (userAnswer || []).includes(answer.id) ? `${textColor}15` : 'transparent',
                                  border: `3px solid ${(userAnswer || []).includes(answer.id) ? textColor : `${textColor}30`}`,
                                  borderRadius: '12px',
                                  padding: '16px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  minHeight: '180px',
                                  textAlign: 'center',
                                  width: '100%'
                                }}
                                onClick={() => {
                                  const currentAnswers = userAnswer || [];
                                  const isSelected = currentAnswers.includes(answer.id);
                                  let newAnswers;
                                  
                                  if (isSelected) {
                                    // Remove the answer if already selected
                                    newAnswers = currentAnswers.filter(id => id !== answer.id);
                                  } else {
                                    // Add the answer if not selected
                                    newAnswers = [...currentAnswers, answer.id];
                                  }
                                  
                                  handleMultipleAnswerChange(question.id, newAnswers);
                                }}
                              >
                                {/* Answer Image */}
                                {shouldShowAnswerImage(answer, 'question') && (
                                  <div
                                    style={{
                                      width: '100%',
                                      height: isMobile ? 120 : 240,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      overflow: 'hidden',
                                      marginBottom: '16px',
                                      borderRadius: '8px'
                                    }}
                                  >
                                    <img
                                      src={answer.image}
                                      alt="Answer"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  </div>
                                )}
                                
                                {/* Option Letter and Text */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <div style={{
                                    backgroundColor: (userAnswer || []).includes(answer.id) ? textColor : 'transparent',
                                    color: (userAnswer || []).includes(answer.id) ? backgroundColor.includes('52c41a') ? '#52c41a' : '#000' : textColor,
                                    border: `2px solid ${textColor}`,
                                    borderRadius: '4px',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    fontSize: answerMetaFontSize,
                                    flexShrink: 0
                                  }}>
                                    {String.fromCharCode(65 + answerIndex)}
                                  </div>
                                  
                                  <Text style={{ 
                                    fontSize: answerFontSize, 
                                    color: textColor, 
                                    fontWeight: '500'
                                  }}>
                                    {answer.text}
                                  </Text>
                                </div>
                                
                                {/* Hidden Checkbox */}
                                <Checkbox value={answer.id} style={{ display: 'none' }} />
                              </div>
                            ))}
                          </div>
                        ) : (
                          // Simple vertical layout for text-only answers
                          <Space direction="vertical" style={{ width: '100%' }}>
                            {getAnswersForQuestion(question).map((answer, answerIndex) => {
                              const isSelected = (userAnswer || []).includes(answer.id);
                              return (
                                <div 
                                  key={answer.id}
                                  className={`answer-option ${blinkingAnswer === `${question.id}-${answer.id}` ? 'blinking-answer' : ''}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px',
                                    border: `2px solid ${isSelected ? textColor : `${textColor}30`}`,
                                    borderRadius: '8px',
                                    backgroundColor: isSelected ? `${textColor}15` : 'transparent',
                                    margin: '8px 0',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    fontSize: answerFontSize,
                                    fontWeight: '500'
                                  }}
                                  onClick={() => {
                                    const currentAnswers = userAnswer || [];
                                    const nextValues = currentAnswers.includes(answer.id)
                                      ? currentAnswers.filter((id) => id !== answer.id)
                                      : [...currentAnswers, answer.id];
                                    handleMultipleAnswerChange(question.id, nextValues);
                                  }}
                                >
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    width: '100%'
                                  }}>
                                    <div style={{
                                      backgroundColor: isSelected ? textColor : 'transparent',
                                      color: isSelected ? (backgroundColor.includes('52c41a') ? '#52c41a' : '#000') : textColor,
                                      border: `2px solid ${textColor}`,
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 'bold',
                                      fontSize: answerMetaFontSize,
                                      flexShrink: 0
                                    }}>
                                      {getAnswerLetter(answerIndex)}
                                    </div>
                                    <Text style={{ color: textColor, fontSize: answerFontSize }}>
                                      {answer.text}
                                    </Text>
                                  </div>
                                  <Checkbox value={answer.id} style={{ display: 'none' }} />
                                </div>
                              );
                            })}
                          </Space>
                        )}
                      </Checkbox.Group>
                    )}
                  </div>

                  {/* Navigation Bar - inside content, 15px below answers */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '1px',
                    paddingBottom: '40px'
                  }}>
                    <div style={navContainerStyle} role="navigation" aria-label="Question navigation">
                      <div style={navContentStyle}>
                        {/* Back Button */}
                        <div
                          onClick={prevStep}
                          style={{
                            width: isMobile ? '56px' : '48px',
                            height: isMobile ? '56px' : '48px',
                            backgroundColor: currentStep === 0 ? '#f5f5f5' : buttonColor,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                            opacity: currentStep === 0 ? 0.5 : 1,
                            transition: 'all 0.3s ease',
                            pointerEvents: 'auto'
                          }}
                        >
                          <ArrowLeftOutlined
                            style={{ 
                              fontSize: '28px',
                              color: currentStep === 0 ? '#bfbfbf' : buttonTextColor,
                              fontWeight: '600'
                            }}
                          />
                        </div>
                        
                        {/* Next Button */}
                        <div
                          onClick={nextStep}
                          style={{
                            width: isMobile ? '56px' : '48px',
                            height: isMobile ? '56px' : '48px',
                            backgroundColor: buttonColor,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            pointerEvents: 'auto'
                          }}
                        >
                          <ArrowRightOutlined
                            style={{ 
                              fontSize: '28px',
                              color: buttonTextColor,
                              fontWeight: '600'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
                </Content>
             
                </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  };

  const renderSubmitConfirmation = () => {
    const totalQuestions = questionnaire.questions.length;
    const answeredCount = Object.keys(userAnswers).length;
    const unansweredCount = totalQuestions - answeredCount;

    const backgroundColor = getDesignValue('backgroundColor', DEFAULT_THEME_BACKGROUND);
    const textColor = getDesignValue('textColor', DEFAULT_THEME_TEXT);
    const buttonColor = resolveButtonColor(textColor);
    const buttonTextColor = resolveButtonTextColor(buttonColor);

    const pageStyle = {
      background: backgroundColor,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? '24px 16px' : '40px 24px',
      color: textColor
    };

    return (
      <div style={pageStyle}>
        <div style={{ textAlign: 'center', maxWidth: '480px', width: '100%' }}>
          <div style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: '700', color: textColor, marginBottom: '12px' }}>
            You're done!
          </div>

          <div style={{ fontSize: isMobile ? '15px' : '17px', color: textColor, opacity: 0.75, marginBottom: '8px' }}>
            You've answered <strong>{answeredCount}</strong> of <strong>{totalQuestions}</strong> questions.
          </div>

          {unansweredCount > 0 && (
            <div style={{
              fontSize: '14px',
              color: '#faad14',
              marginBottom: '8px',
              padding: '8px 16px',
              background: 'rgba(250,173,20,0.12)',
              borderRadius: '8px',
              display: 'inline-block'
            }}>
              ⚠️ {unansweredCount} question{unansweredCount > 1 ? 's' : ''} not answered
            </div>
          )}

          <div style={{ fontSize: '14px', color: textColor, opacity: 0.6, marginBottom: '32px' }}>
            Once submitted you won't be able to change your answers.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={() => completeQuiz()}
              style={{
                width: '100%',
                maxWidth: '320px',
                padding: '16px 32px',
                fontSize: '17px',
                fontWeight: '700',
                backgroundColor: buttonColor,
                color: buttonTextColor,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                letterSpacing: '0.3px'
              }}
            >
              Submit Quiz
            </button>

            <button
              onClick={() => setShowSubmitConfirmation(false)}
              style={{
                background: 'transparent',
                border: `1px solid ${textColor}40`,
                color: textColor,
                padding: '10px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: 'pointer',
                opacity: 0.8
              }}
            >
              ← Review my answers
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderResultsScreen = () => {
    const totalQuestions = questionnaire.questions.length;
    const scoreColor = finalScore >= 80 ? '#52c41a' : '#000000';
    
    // Results screen uses white background and black text (no theme colors)
    const backgroundColor = '#ffffff';
    const textColor = '#000000';
    // Only use button color from organization/business unit
    const buttonColor = '#1890ff';
    const buttonTextColor = '#ffffff';
    
    const pageStyle = {
      background: backgroundColor,
      minHeight: '100vh',
      padding: isMobile ? '16px' : '20px',
      paddingBottom: isMobile ? '80px' : '120px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: isMobile ? '24px' : '40px',
      color: textColor,
      ...getTransitionStyle()
    };

    const containerStyle = {
      backgroundColor: 'transparent',
      padding: isMobile ? '24px' : '40px',
      color: textColor,
      maxWidth: '100%',
      textAlign: 'center',
      marginBottom: '24px'
    };
    
    return (
      <div style={pageStyle}>
        <Content style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          maxWidth: '600px',
          width: '100%',
          textAlign: 'center'
        }}>
          <div style={containerStyle}>
            {/* Celebration GIF - displayed randomly above "Nicely done!" */}
            {celebrationGif && (
              <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'center' }}>
                <img 
                  src={celebrationGif.url} 
                  alt="Celebration" 
                  style={{
                    maxWidth: isMobile ? '95%' : '300px',
                    maxHeight: isMobile ? '250px' : '240px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            
            <Title level={2} style={{ color: textColor, fontSize: isMobile ? '1.5rem' : '2rem', marginBottom: '16px' }}>
              Nicely done {isCampaignMode && campaignData?.participantId?.name ? campaignData.participantId.name : ''}!
            </Title>
            
            <Text style={{ 
              fontSize: questionFontSize, 
              color: textColor, 
              opacity: 0.8,
              display: 'block',
              marginBottom: '8px'
            }}>
              You scored:
            </Text>
            
            <Title level={1} style={{ 
              color: scoreColor, 
              margin: 0,
              fontSize: isMobile ? '48px' : '64px',
              marginBottom: '32px'
            }}>
              {finalScore}%
            </Title>

            <Title level={3} style={{ 
              color: textColor, 
              fontWeight: 'bold', 
              marginBottom: '16px',
              fontSize: isMobile ? '1.2rem' : '1.5rem'
            }}>
              Quiz Answers and Leaderboard
            </Title>
            
            <Text style={{
              color: textColor,
              fontSize: questionFontSize,
              opacity: 0.8,
              display: 'block',
              textAlign: 'center',
              marginBottom: '1px',
              lineHeight: '1.6'
            }}>
              Scroll down to see the correct answers, how you did, and learn a little more about each question (this is where the learning happens). Scroll a little further and take a look at your position on the leaderboard.
            </Text>
          </div>

            {/* Detailed Answer Review Section */}
            <div style={{ 
              marginTop: '4px', 
              marginBottom: '40px',
              textAlign: 'left',
              maxWidth: '800px',
              width: '100%'
            }}>
            
              {questionnaire.questions.map((question, questionIndex) => {
                const userAnswer = userAnswers[question.id];
                const correctAnswers = question.answers.filter(answer => answer.isCorrect);
                const userSelectedAnswers = question.type === 'single' 
                  ? [question.answers.find(answer => answer.id === userAnswer)]
                  : question.answers.filter(answer => userAnswer?.includes(answer.id));
                const focusX = typeof question.imageFocus?.x === 'number' ? question.imageFocus.x : 50;
                const focusY = typeof question.imageFocus?.y === 'number' ? question.imageFocus.y : 50;
                const imageObjectPosition = `${focusX}% ${focusY}%`;
                const videoEmbedUrl = getYouTubeEmbedUrl(question.videoUrl);
                // Respect imageVisibility: hide question media when set to 'question' only
                const questionVisibility = question.imageVisibility || 'both';
                const showMediaOnResults = questionVisibility === 'both' || questionVisibility === 'results';
                const hasVideo = Boolean(videoEmbedUrl) && showMediaOnResults;
                const hasImage = Boolean(question?.image) && showMediaOnResults;
                const hasMedia = hasVideo || hasImage;
                
                // Determine if question was answered correctly
                let isCorrect = false;
                if (question.type === 'single') {
                  const selectedAnswer = question.answers.find(answer => answer.id === userAnswer);
                  isCorrect = selectedAnswer?.isCorrect || false;
                } else {
                  const correctIds = correctAnswers.map(answer => answer.id);
                  const selectedIds = userAnswer || [];
                  isCorrect = correctIds.length === selectedIds.length && 
                             correctIds.every(id => selectedIds.includes(id)) &&
                             selectedIds.every(id => correctIds.includes(id));
                }

                return (
                  <div key={question.id} style={{ 
                    marginBottom: '48px'
                  }}>
                    {/* Question Image - Centered like in the reference */}
                    {hasMedia && (
                      <div style={{ 
                        textAlign: 'center',
                        marginBottom: '24px'
                      }}>
                        {hasVideo ? (
                          <div style={{
                            width: '100%',
                            maxWidth: '640px',
                            margin: '0 auto',
                            position: 'relative',
                            paddingTop: '56.25%',
                            borderRadius: '16px',
                            overflow: 'hidden',
                            boxShadow: '0 20px 45px rgba(0, 0, 0, 0.25)'
                          }}>
                            <iframe
                              src={videoEmbedUrl}
                              title={`Question ${questionIndex + 1} video`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 0
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        ) : (
                          <Image
                            src={question.image}
                            alt={`Question ${questionIndex + 1}`}
                            preview
                            style={{
                              maxWidth: '400px',
                              width: '100%',
                              borderRadius: '8px'
                            }}
                            imgStyle={{
                              width: '100%',
                              height: '100%',
                              maxHeight: '320px',
                              objectFit: 'cover',
                              objectPosition: imageObjectPosition
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* Question Header */}
                    <Title level={4} style={{ 
                      color: textColor, 
                      fontSize: '20px',
                      fontWeight: 'bold',
                      marginBottom: '12px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      QUESTION {questionIndex + 1} ANSWER
                    </Title>

                    {/* Question Title */}
                    <Text style={{ 
                      color: textColor, 
                      fontSize: '18px',
                      display: 'block',
                      marginBottom: '24px',
                      lineHeight: '1.4'
                    }}>
                      {question.title}
                    </Text>

                    {/* Answer Options - Bullet Point Style */}
                    <div style={{ marginBottom: '24px' }}>
                      {question.answers.map((answer, answerIndex) => {
                        const isUserSelected = question.type === 'single' 
                          ? userAnswer === answer.id
                          : (userAnswer || []).includes(answer.id);
                        const optionLetter = getAnswerLetter(answerIndex);
                        
                        return (
                          <div key={answer.id} style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            marginBottom: '12px',
                            padding: '8px 0'
                          }}>
                            {/* Bullet Point, Check Mark, or Cross */}
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '4px',
                              backgroundColor: answer.isCorrect ? '#2f7e07ff' : (isUserSelected && !answer.isCorrect ? '#b41316ff' : 'transparent'),
                              border: answer.isCorrect || (isUserSelected && !answer.isCorrect) ? 'none' : `2px solid ${textColor}40`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '2px',
                              flexShrink: 0
                            }}>
                              {answer.isCorrect ? (
                                <CheckOutlined style={{ 
                                  color: 'white', 
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }} />
                              ) : isUserSelected ? (
                                <CloseOutlined style={{ 
                                  color: 'white', 
                                  fontSize: '14px',
                                  fontWeight: 'bold'
                                }} />
                              ) : (
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: `${textColor}60`
                                }} />
                              )}
                            </div>

                            {/* Answer Text with Status */}
                            <div style={{ flex: 1 }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                flexWrap: 'wrap'
                              }}>
                                <div style={{
                                  border: `2px solid ${textColor}`,
                                  borderRadius: '50%',
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  fontSize: answerMetaFontSize,
                                  color: textColor,
                                  backgroundColor: answer.isCorrect ? `${textColor}20` : 'transparent',
                                  flexShrink: 0
                                }}>
                                  {optionLetter}
                                </div>
                                <Text style={{ 
                                  color: textColor, 
                                  fontSize: answerFontSize,
                                  fontWeight: answer.isCorrect ? 'bold' : 'normal',
                                  lineHeight: '1.5'
                                }}>
                                  {answer.text}
                                  {isUserSelected && answer.isCorrect && (
                                    <Text style={{
                                      color: '#2f7e07ff',
                                      fontSize: answerMetaFontSize,
                                      fontWeight: 'bold',
                                      marginLeft: '12px'
                                    }}>
                                      (Your Answer - Correct)
                                    </Text>
                                  )}
                                </Text>
                              </div>
                              
                              {/* Your Answer - Incorrect label on new line */}
                              {isUserSelected && !answer.isCorrect && (
                                <div style={{ marginTop: '4px', marginLeft: '44px' }}>
                                  <Text style={{
                                    color: '#b41316ff',
                                    fontSize: answerMetaFontSize,
                                    fontWeight: 'bold'
                                  }}>
                                    Your answer - Incorrect
                                  </Text>
                                </div>
                              )}
                              
                              {/* Answer Image */}
                              {shouldShowAnswerImage(answer, 'results') && (
                                <div style={{ marginTop: '8px' }}>
                                  <img 
                                    src={answer.image} 
                                    alt="Answer"
                                    style={{ 
                                      width: '200px', 
                                      height: '200px',
                                      borderRadius: '1px',
                                      objectFit: 'cover',
                                      cursor: 'pointer'
                                    }}
                                    onClick={() => window.open(answer.image, '_blank')}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation Section */}
                    {correctAnswers.some(answer => answer.explanation) && (
                      <div style={{
                        marginTop: '24px',
                        padding: '16px',
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderLeft: `4px solid #2f7e07ff`
                      }}>
                        {correctAnswers.filter(answer => answer.explanation).map((answer) => (
                          <div 
                            key={answer.id}
                            className="rich-text-content"
                            style={{ 
                              color: textColor, 
                              fontSize: answerFontSize,
                              lineHeight: '1.6'
                            }}
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer.explanation) }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Leaderboard Section */}
            {isCampaignMode && leaderboardData?.enabled && (
              <div style={{
                marginTop: '48px',
                marginBottom: '32px',
                width: '100%'
              }}>
                {/* Header */}
                <div style={{ 
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <Text style={{ 
                    color: scoreColor, 
                    fontSize: questionFontSize,
                    fontWeight: '600',
                    display: 'block'
                  }}>
                    <TrophyOutlined style={{ marginRight: '8px' }} />
                    Leaderboard
                  </Text>
                  <Text style={{
                    color: textColor,
                    opacity: 0.6,
                    fontSize: answerFontSize
                  }}>
                    {leaderboardData.leaderboardType === 'latest-campaign' && leaderboardData.campaignName
                      ? leaderboardData.campaignName
                      : leaderboardData.department}
                  </Text>
                </div>

                {leaderboardLoading ? (
                  <div style={{ textAlign: 'center', padding: '24px' }}>
                    <Spin size="small" />
                  </div>
                ) : leaderboardData.leaderboard?.length > 0 ? (
                  <div style={{
                    border: `1px solid ${textColor}20`,
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    {/* Campaign Breakdown View */}
                    {leaderboardData.leaderboardType === 'campaign-breakdown' ? (
                      <>
                        {/* Scrollable table for campaign breakdown */}
                        <div style={{ overflowX: 'auto', width: '100%' }}>
                          {/* Table Header */}
                          <div style={{
                            display: 'flex',
                            padding: '12px 16px',
                            backgroundColor: `${textColor}08`,
                            borderBottom: `1px solid ${textColor}15`,
                            fontSize: answerMetaFontSize,
                            fontWeight: '600',
                            color: textColor,
                            opacity: 0.7,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            minWidth: '100%'
                          }}>
                            <div style={{ width: '35px', flexShrink: 0 }}>#</div>
                            <div style={{ flex: '1 1 100px', minWidth: '80px' }}>Name</div>
                            {leaderboardData.campaignNames?.map((name, i) => (
                              <div key={i} style={{ flex: '1 1 60px', minWidth: '50px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '9px' }} title={name}>
                                {name.length > 8 ? name.slice(0, 8) + '..' : name}
                              </div>
                            ))}
                            <div style={{ width: '55px', flexShrink: 0, textAlign: 'right' }}>Avg</div>
                          </div>
                          
                          {/* Leaderboard Entries */}
                          {leaderboardData.leaderboard.map((entry, index) => (
                            <div 
                              key={index}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '14px 16px',
                                backgroundColor: entry.isCurrentUser ? `${scoreColor}15` : 'transparent',
                                borderBottom: index < leaderboardData.leaderboard.length - 1 ? `1px solid ${textColor}10` : 'none',
                                minWidth: '100%'
                              }}
                            >
                              {/* Rank */}
                              <div style={{
                                width: '35px',
                                flexShrink: 0,
                                fontWeight: entry.rank <= 3 ? '700' : '500',
                                fontSize: answerFontSize,
                                color: entry.rank === 1 ? '#D4AF37' : entry.rank === 2 ? '#8A8A8A' : entry.rank === 3 ? '#A0522D' : textColor
                              }}>
                                {entry.rank}
                              </div>

                              {/* Name */}
                              <div style={{ flex: '1 1 80px', minWidth: 0, overflow: 'hidden' }}>
                                <Text style={{ 
                                  color: textColor, 
                                  fontWeight: entry.isCurrentUser ? '600' : '400',
                                  fontSize: answerMetaFontSize,
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {entry.name}{entry.isCurrentUser ? ' (You)' : ''}
                                </Text>
                              </div>

                              {/* Campaign scores */}
                              {leaderboardData.campaignNames?.map((name, i) => (
                                <div key={i} style={{ 
                                  flex: '1 1 60px',
                                  minWidth: '50px',
                                  textAlign: 'center',
                                  fontSize: answerFontSize,
                                  color: entry.campaigns?.[name] !== undefined ? textColor : `${textColor}40`
                                }}>
                                  {entry.campaigns?.[name] !== undefined ? `${entry.campaigns[name]}%` : '-'}
                                </div>
                              ))}

                              {/* Average */}
                              <div style={{ 
                                width: '55px',
                                flexShrink: 0,
                                textAlign: 'right',
                                fontWeight: '600',
                                fontSize: answerFontSize,
                                color: textColor
                              }}>
                                {entry.avgScore}%
                              </div>
                            </div>
                          ))}

                          {/* Current user position if not in top list */}
                          {leaderboardData.currentUserRank && !leaderboardData.leaderboard.some(e => e.isCurrentUser) && (
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '14px 16px',
                              backgroundColor: `${scoreColor}15`,
                              borderTop: `1px solid ${textColor}15`,
                              minWidth: '100%'
                            }}>
                              <div style={{ width: '35px', flexShrink: 0, fontWeight: '500', fontSize: answerFontSize, color: textColor }}>
                                {leaderboardData.currentUserRank}
                              </div>
                              <div style={{ flex: '1 1 100px', minWidth: '80px' }}>
                                <Text style={{ color: textColor, fontWeight: '600', fontSize: answerFontSize }}>
                                  You
                                </Text>
                              </div>
                              {leaderboardData.campaignNames?.map((name, i) => (
                                <div key={i} style={{ 
                                  flex: '1 1 60px',
                                  minWidth: '50px',
                                  textAlign: 'center',
                                  fontSize: answerFontSize,
                                  color: leaderboardData.currentUserCampaigns?.[name] !== undefined ? textColor : `${textColor}40`
                                }}>
                                  {leaderboardData.currentUserCampaigns?.[name] !== undefined ? `${leaderboardData.currentUserCampaigns[name]}%` : '-'}
                                </div>
                              ))}
                              <div style={{ width: '55px', flexShrink: 0, textAlign: 'right', fontWeight: '600', fontSize: answerFontSize, color: textColor }}>
                                {leaderboardData.currentUserAvgScore || 0}%
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Points/Presentation View */}
                        {/* Table Header */}
                        <div style={{
                          display: 'flex',
                          padding: '12px 16px',
                          backgroundColor: `${textColor}08`,
                          borderBottom: `1px solid ${textColor}15`,
                          fontSize: answerMetaFontSize,
                          fontWeight: '600',
                          color: textColor,
                          opacity: 0.7,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          <div style={{ width: '40px' }}>#</div>
                          <div style={{ flex: 1 }}>Name</div>
                          <div style={{ width: '60px', textAlign: 'center' }}>Quizzes</div>
                          <div style={{ width: '60px', textAlign: 'center' }}>Avg</div>
                          {leaderboardData.leaderboardType === 'points-percentage' && (
                            <div style={{ width: '70px', textAlign: 'right' }}>Points</div>
                          )}
                        </div>
                        
                        {/* Leaderboard Entries */}
                        {leaderboardData.leaderboard.map((entry, index) => (
                          <div 
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '14px 16px',
                              backgroundColor: entry.isCurrentUser ? `${scoreColor}15` : 'transparent',
                              borderBottom: index < leaderboardData.leaderboard.length - 1 ? `1px solid ${textColor}10` : 'none'
                            }}
                          >
                            {/* Rank */}
                            <div style={{
                              width: '40px',
                              fontWeight: entry.rank <= 3 ? '700' : '500',
                              fontSize: answerFontSize,
                              color: entry.rank === 1 ? '#D4AF37' : entry.rank === 2 ? '#8A8A8A' : entry.rank === 3 ? '#A0522D' : textColor
                            }}>
                              {entry.rank}
                            </div>

                            {/* Name */}
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <Text style={{ 
                                color: textColor, 
                                fontWeight: entry.isCurrentUser ? '600' : '400',
                                fontSize: answerMetaFontSize,
                                display: 'block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {entry.name}{entry.isCurrentUser ? ' (You)' : ''}
                              </Text>
                            </div>

                            {/* Quizzes completed */}
                            <div style={{ 
                              width: '60px', 
                              textAlign: 'center',
                              fontSize: answerFontSize,
                              color: textColor
                            }}>
                              {entry.completions}
                            </div>

                            {/* Average score - always show */}
                            <div style={{ 
                              width: '60px', 
                              textAlign: 'center',
                              fontSize: answerFontSize,
                              color: textColor
                            }}>
                              {entry.avgScore}%
                            </div>

                            {/* Weighted Points - only for points-percentage */}
                            {leaderboardData.leaderboardType === 'points-percentage' && (
                              <div style={{ 
                                width: '70px', 
                                textAlign: 'right',
                                fontWeight: '600',
                                fontSize: answerFontSize,
                                color: textColor
                              }}>
                                {entry.weightedScore}
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Current user position if not in top list */}
                        {leaderboardData.currentUserRank && !leaderboardData.leaderboard.some(e => e.isCurrentUser) && (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '14px 16px',
                            backgroundColor: `${scoreColor}15`,
                            borderTop: `1px solid ${textColor}15`
                          }}>
                            <div style={{ width: '40px', fontWeight: '500', fontSize: answerFontSize, color: textColor }}>
                              {leaderboardData.currentUserRank}
                            </div>
                            <div style={{ flex: 1 }}>
                              <Text style={{ color: textColor, fontWeight: '600', fontSize: answerFontSize }}>
                                You
                              </Text>
                            </div>
                            <div style={{ width: '60px', textAlign: 'center', fontSize: answerFontSize, color: textColor }}>
                              {leaderboardData.currentUserCompletions || 0}
                            </div>
                            <div style={{ width: '60px', textAlign: 'center', fontSize: answerFontSize, color: textColor }}>
                              {leaderboardData.currentUserAvgScore || 0}%
                            </div>
                            {leaderboardData.leaderboardType === 'points-percentage' && (
                              <div style={{ width: '70px', textAlign: 'right', fontWeight: '600', fontSize: answerFontSize, color: textColor }}>
                                {leaderboardData.currentUserWeightedScore || 0}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '32px',
                    border: `1px solid ${textColor}20`,
                    borderRadius: '12px'
                  }}>
                    <Text style={{ color: textColor, opacity: 0.6, fontSize: answerFontSize }}>
                      You're the first to complete a quiz this month!
                    </Text>
                  </div>
                )}


              </div>
            )}

            <Space>
              {/* <Button
                onClick={closePreview}
                style={{ 
                  fontSize: buttonFontSize, 
                  height: '48px', 
                  padding: '0 32px',
                  borderRadius: '8px',
                  backgroundColor: buttonColor,
                  borderColor: buttonColor,
                  color: buttonTextColor,
                  fontWeight: '600'
                }}
              >
                Close
              </Button> */}

              <Button
                onClick={restartQuiz}
                style={{
                  fontSize: buttonFontSize, 
                  height: '48px', 
                  padding: '0 24px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  borderColor: buttonTextColor,
                  color: buttonTextColor,
                  fontWeight: '700'
                }}
              >
                Restart
              </Button>
            </Space>
        </Content>
      </div>
    );
  };

  const renderIntroductionPage = () => {
    const introPage = getIntroductionPages()[getCurrentIntroPageIndex()];
    const introPageHtml = introPage?.content || introPage?.text || introPage?.description || '';
    const isLastIntroPage = currentStep === getTotalIntroSteps() - 1;

    if (!introPage) {
      return renderIntroScreen();
    }
    
    // Use business unit theme for campaign mode, otherwise use questionnaire theme
    const backgroundColor = getDesignValue('backgroundColor', DEFAULT_THEME_BACKGROUND);
    const textColor = getDesignValue('textColor', DEFAULT_THEME_TEXT);
    const buttonColor = resolveButtonColor(textColor);
    const buttonTextColor = resolveButtonTextColor(buttonColor);

    return (
      <div style={{
        background: backgroundColor,
        minHeight: isMobile ? 'auto' : '100vh',
        display: 'flex',
        ...getTransitionStyle()
      }}>
        <Row style={{
          minHeight: isMobile ? '320px' : '100vh',
          height: isMobile ? '320px' : '100vh',
          width: '100%'
        }}>
          {/* Right side - Image (only if image exists) - Order changed for mobile */}
          {introPage?.image && (
            <Col
              xs={{ span: 24, order: 1 }}
              lg={{ span: 12, order: 2 }}
              style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              padding: '0px',
              minHeight: isMobile ? '320px' : '100vh',
              height: isMobile ? '320px' : '100vh',
              boxSizing: 'border-box'
            }}
            >
              <div style={{
                width: '100%',
                height: '100%',
                minHeight: isMobile ? '320px' : '100vh',
                overflow: 'hidden'
              }}>
                <img
                  src={introPage.image}
                  alt="Introduction"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </div>
            </Col>
          )}

          {/* Left side - Content - Order changed for mobile */}
          <Col 
            xs={{ span: 24, order: introPage?.image ? 2 : 1 }}
            lg={{ span: introPage?.image ? 12 : 24, order: introPage?.image ? 1 : 1 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: isMobile ? 'flex-start' : 'center',
              alignItems: isMobile ? 'center' : 'flex-start',
              padding: isMobile ? '24px' : '0px',
              paddingBottom: isMobile ? '48px' : '120px',
              minHeight: isMobile ? 'auto' : '100vh',
              height: isMobile ? 'auto' : '100vh',
              boxSizing: 'border-box'
            }}
          >
            <Content style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              minHeight: isMobile ? 'auto' : '100vh',
              height: isMobile ? 'auto' : '100vh',
              width: '100%'
            }}>
              <div style={{ 
                maxWidth: '600px', 
                width: '100%', 
                textAlign: 'left', 
                padding: isMobile ? '24px' : '40px' 
              }}>

                {/* Business Unit Logo - Always show for campaign mode */}
                {isCampaignMode && campaignData?.participantId?.businessUnit && (
                  <div style={{ textAlign: 'left', marginBottom: '32px' }}>
                    {campaignData.participantId.businessUnit.logo && (
                      <div style={{ marginBottom: '16px' }}>
                        <img
                          src={campaignData.participantId.businessUnit.logo}
                          alt={campaignData.participantId.businessUnit.name}
                          style={{ 
                            maxHeight: '80px', 
                            maxWidth: '160px',
                            objectFit: 'contain'
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {/* Description/Content */}
                <div style={{ marginBottom: '32px' }}>
                  {renderHtmlContent(introPageHtml, {
                    color: textColor,
                    fontSize: questionFontSize,
                    lineHeight: '1.6',
                    wordBreak: 'break-word'
                  }) || (
                    <Text style={{ 
                      color: textColor, 
                      fontSize: questionFontSize,
                      lineHeight: '1.6',
                      display: 'block'
                    }}>
                      No content available for this introduction page.
                    </Text>
                  )}
                </div>
                
                {/* Navigation Buttons */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: '16px',
                  marginTop: '20px'
                }}>
                  <Button
                    
                    size="large"
                    icon={isLastIntroPage ? <PlayCircleOutlined /> : <ArrowRightOutlined />}
                    onClick={() => setCurrentStep(currentStep + 1)}
                    style={{ 
                      margin: '10px',
                      backgroundColor: buttonColor,
                      borderColor: buttonColor,
                      color: buttonTextColor,
                      fontWeight: '700',
                      fontSize: buttonFontSize
                    }}
                  >
                    {introPage?.buttonText || (isLastIntroPage ? "Start Questions" : 'Next')}
                  </Button>
                </div>
              </div>
            </Content>
          </Col>
        </Row>
      </div>
    );
  };  
  
  // Get colors for loading/error states (use defaults if questionnaire not loaded)
  // Use business unit theme for campaign mode, otherwise use questionnaire theme
  const backgroundColor = getDesignValue('backgroundColor', DEFAULT_THEME_BACKGROUND);
  const textColor = getDesignValue('textColor', DEFAULT_THEME_TEXT);
  const buttonColor = resolveButtonColor(textColor);
  const buttonTextColor = resolveButtonTextColor(buttonColor);

  // Keep the browser chrome background aligned with the themed pages to avoid white flashes on overscroll
  useEffect(() => {
    if (!backgroundColor) return;

    const previousBodyBg = document.body.style.background;
    const previousHtmlBg = document.documentElement.style.background;
    const previousOverscroll = document.body.style.overscrollBehavior;

    document.body.style.background = backgroundColor;
    document.documentElement.style.background = backgroundColor;
    document.body.style.overscrollBehavior = 'contain';

    return () => {
      document.body.style.background = previousBodyBg;
      document.documentElement.style.background = previousHtmlBg;
      document.body.style.overscrollBehavior = previousOverscroll;
    };
  }, [backgroundColor]);

  if (loading) {
    return (
      <div style={{ 
        background: '#ffffff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666666',
        padding: '20px',
        textAlign: 'center'
      }}>
        <Spin size="large" style={{ marginBottom: '20px' }} />
        <Text style={{ color: '#666666', fontSize: '18px', opacity: 0.8 }}>
          {isCampaignMode ? 'Loading your quiz...' : 'Loading quiz preview...'}
        </Text>
        <Text style={{ color: '#999999', fontSize: '14px', opacity: 0.6, marginTop: '8px' }}>
          This may take a moment on slower connections
        </Text>
      </div>
    );
  }

  if (error || !questionnaire) {
    return (
      <div style={{ 
        background: backgroundColor,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        textAlign: 'center',
        color: textColor
      }}>
        <ExclamationCircleOutlined style={{ fontSize: '48px', color: textColor, marginBottom: '16px', opacity: 0.8 }} />
        <Text style={{ color: textColor, fontSize: '18px', marginBottom: '8px' }}>
          {error || 'Failed to load quiz'}
        </Text>
        <Text style={{ color: textColor, fontSize: '14px', marginBottom: '24px', opacity: 0.7 }}>
          Please check your internet connection and try again
        </Text>
        {isCampaignMode ? (
          <Button 
            onClick={() => window.location.reload()}
            icon={<ReloadOutlined />}
            style={{
              backgroundColor: buttonColor,
              borderColor: buttonColor,
              color: buttonTextColor,
              fontWeight: '600'
            }}
          >
            Retry
          </Button>
        ) : (
          <Button 
            onClick={() => window.close()}
            style={{
              backgroundColor: buttonColor,
              borderColor: buttonColor,
              color: buttonTextColor,
              fontWeight: '600'
            }}
          >
            Close Preview
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <style jsx>{`
        @keyframes answerBlink {
          0% { opacity: 1; transform: scale(1); }
          25% { opacity: 0.7; transform: scale(1.05); }
          50% { opacity: 1; transform: scale(1.1); }
          75% { opacity: 0.7; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        
        @keyframes fadeSlideUp {
          0% { 
            opacity: 0; 
            transform: translateY(30px);
          }
          100% { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        
        .question-fade-in {
          animation: fadeSlideUp 0.4s ease-out forwards;
        }
        
        .blinking-answer {
          animation: answerBlink 0.3s ease-in-out;
        }
        
        .answer-option {
          transition: all 0.2s ease;
        }
        
        .answer-option:hover {
          transform: translateY(-2px);
        }
      `}</style>
      <div key={currentStep} className="question-fade-in">
      {(() => {
        if (showSubmitConfirmation) {
          return renderSubmitConfirmation();
        } else if (currentStep === 0) {
          return renderIntroScreen();
        } else if (isIntroductionPage()) {
          return renderIntroductionPage();
        } else if (currentStep === -1) {
          return renderResultsScreen();
        } else {
          const currentQuestionIndex = getCurrentQuestionIndex();
          const currentQuestion = questionnaire?.questions?.[currentQuestionIndex];

          if (!currentQuestion) {
            return (
              <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: backgroundColor,
                color: textColor
              }}>
                <div style={{ textAlign: 'center' }}>
                  <Spin tip="Preparing next question..." size="large" />
                  <Text style={{ display: 'block', marginTop: 12, color: textColor }}>
                    {questionsLoading
                      ? 'Loading questions'
                      : 'Hold on while we prepare the next question'}
                  </Text>
                </div>
              </div>
            );
          }

          return renderQuestion(currentQuestion, currentQuestionIndex);
        }
      })()}
      </div>
    </div>
  );
};

export default QuestionnairePreviewPage;